import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FaChevronRight, FaFileExcel, FaPlus, FaSearch, FaUpload } from "react-icons/fa";
import { bulkUploadHospitals, downloadHospitalTemplate, editHospital, getHospitalBulkUploadStatus, getHospitalsList } from "../services/hospitalService";
import { useAuth } from "../auth/AuthContext";
import { HOSPITAL_LIST_URL_KEY } from "../utils/subscriberHelpers";

const isHospitalActive = (hospital) => hospital?.active !== false;

const ConfirmStatusModal = ({ hospital, disable, submitting, error, onCancel, onConfirm }) => {
  if (!hospital) return null;
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {disable ? "Disable hospital" : "Enable hospital"}
          </h3>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-gray-600">
            Are you sure you want to {disable ? "disable" : "enable"}{" "}
            <span className="font-medium text-gray-900">{hospital.name || "this hospital"}</span>?
            {disable && " It will no longer be available for new bookings."}
          </p>
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 ${
              disable ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {submitting ? "Saving..." : disable ? "Yes, disable" : "Yes, enable"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const getCurrentRole = () => {
  const authRole = localStorage.getItem("authRole");
  const roleMapping = {
    wallet_superadmin: "SuperAdmin",
    wallet_finance: "Finance",
    wallet_ops: "Ops",
    wallet_support: "Support",
  };
  return roleMapping[authRole] || null;
};

// Scroll offset saved when a row is opened, keyed by the list URL it was opened from,
// so Back lands on the same rows rather than the top of the page.
const SCROLL_KEY = "hospitalList:scroll";

const HospitalList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const currentRole = getCurrentRole();
  const canAccessHospitals = currentRole === "Ops" || currentRole === "SuperAdmin";
  const canCreateHospital = canAccessHospitals;
  const canBulkUploadHospitals = canAccessHospitals;
  const PAGE_SIZE = 20;

  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(() => Number(searchParams.get("page") || 1));
  const [nameInput, setNameInput] = useState(() => searchParams.get("name") || "");
  const [cityInput, setCityInput] = useState(() => searchParams.get("city") || "");
  // Debounced values — only these drive the API call
  const [nameFilter, setNameFilter] = useState(() => searchParams.get("name") || "");
  const [cityFilter, setCityFilter] = useState(() => searchParams.get("city") || "");
  const [pagination, setPagination] = useState({
    page: 1,
    items: PAGE_SIZE,
    total: 0,
    total_pages: 0,
  });
  const debounceRef = useRef(null);
  const fileInputRef = useRef(null);

  const [templateLoading, setTemplateLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadJob, setUploadJob] = useState(null);
  const pollRef = useRef(null);

  // Hospital to toggle active status for (drives the confirmation modal)
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [statusError, setStatusError] = useState("");

  const handleConfirmStatusToggle = async () => {
    if (!statusTarget) return;
    const hospitalId = statusTarget.id || statusTarget.hospital_id;
    const nextActive = !isHospitalActive(statusTarget);

    setStatusSubmitting(true);
    setStatusError("");
    try {
      await editHospital(currentRole, hospitalId, {
        name: statusTarget.name,
        active: nextActive,
      });
      setHospitals((prev) =>
        prev.map((h) =>
          (h.id || h.hospital_id) === hospitalId ? { ...h, active: nextActive } : h
        )
      );
      setStatusTarget(null);
    } catch (err) {
      setStatusError(err.message || "Failed to update hospital status.");
    } finally {
      setStatusSubmitting(false);
    }
  };

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set("page", String(page));
    if (nameFilter.trim()) params.set("name", nameFilter.trim());
    if (cityFilter.trim()) params.set("city", cityFilter.trim());
    setSearchParams(params, { replace: true });
  }, [page, nameFilter, cityFilter, setSearchParams]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Inputs already match the applied filters (first render, or Back from a hospital) —
    // nothing changed, so keep the page from the URL instead of resetting it to 1.
    if (nameInput.trim() === nameFilter && cityInput.trim() === cityFilter) return undefined;
    debounceRef.current = setTimeout(() => {
      setNameFilter(nameInput.trim());
      setCityFilter(cityInput.trim());
      setPage(1);
    }, 700);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameInput, cityInput]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !canAccessHospitals) return;

    const loadHospitals = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getHospitalsList(currentRole, {
          page,
          items: PAGE_SIZE,
          name: nameFilter,
          city: cityFilter,
        });

        const items = response?.msg?.items || response?.items || [];
        const pg = response?.msg?.pagination || response?.pagination;

        setHospitals(Array.isArray(items) ? items : []);
        if (pg) {
          const totalPages = Math.ceil((pg.total || 0) / (pg.items || PAGE_SIZE));
          setPagination({ ...pg, total_pages: totalPages });
        } else {
          setPagination({
            page,
            items: PAGE_SIZE,
            total: items.length,
            total_pages: Math.ceil(items.length / PAGE_SIZE),
          });
        }
      } catch (err) {
        setError(err.message || "Failed to load hospitals.");
        setHospitals([]);
      } finally {
        setLoading(false);
      }
    };

    loadHospitals();
  }, [authLoading, isAuthenticated, canAccessHospitals, currentRole, page, nameFilter, cityFilter]);

  // Restore the scroll offset once the rows it was measured against are back.
  useEffect(() => {
    if (loading || hospitals.length === 0) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || "null");
      if (saved) sessionStorage.removeItem(SCROLL_KEY);
      if (!saved || saved.search !== location.search) return;
      // The table settles over a few frames; keep nudging until the page is tall enough.
      let frames = 0;
      const restore = () => {
        window.scrollTo(0, saved.y);
        if (Math.abs(window.scrollY - saved.y) > 2 && frames++ < 30) requestAnimationFrame(restore);
      };
      requestAnimationFrame(restore);
    } catch {
      // sessionStorage unavailable — Back still keeps page and filters via the URL.
    }
  }, [loading, hospitals, location.search]);

  const openHospital = (path, state) => {
    const from = `${location.pathname}${location.search}`;
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ search: location.search, y: window.scrollY }));
      sessionStorage.setItem(HOSPITAL_LIST_URL_KEY, from);
    } catch {
      // ignore — scroll restore is a convenience
    }
    navigate(path, { state: { ...state, from } });
  };

  const handleDownloadTemplate = async () => {
    setTemplateLoading(true);
    try {
      const blob = await downloadHospitalTemplate(currentRole);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "hospital_onboarding_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "Failed to download template.");
    } finally {
      setTemplateLoading(false);
    }
  };

  const refreshHospitalList = () => {
    setPage(1);
    setNameFilter("");
    setCityFilter("");
    setNameInput("");
    setCityInput("");
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const resp = await getHospitalBulkUploadStatus(currentRole, jobId);
        const status = resp?.msg || resp;
        setUploadJob(status);

        const terminal = ["success", "partial", "failed"].includes(status?.status);
        if (terminal) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setUploadLoading(false);
          setUploadResult({ success: status.status !== "failed", data: status });
          if ((status?.rows_created || 0) > 0) refreshHospitalList();
        }
      } catch (err) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setUploadLoading(false);
        setUploadResult({ success: false, error: err.message || "Failed to fetch job status." });
      }
    }, 3000);
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    setUploadLoading(true);
    setUploadResult(null);
    setUploadJob(null);
    try {
      const response = await bulkUploadHospitals(currentRole, file);
      const enqueued = response?.msg || response;
      const jobId = enqueued?.job_id;
      if (!jobId) throw new Error("Server did not return a job id.");
      setUploadJob({ id: jobId, status: "pending", total_rows: enqueued.total_rows || 0, rows_processed: 0, percent: 0 });
      startPolling(jobId);
    } catch (err) {
      setUploadResult({ success: false, error: err.message || "Upload failed." });
      setUploadLoading(false);
    }
  };

  if (!canAccessHospitals) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You do not have permission to access hospitals.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate("/subscriber-wallet")}
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Home
          </button>
        </div>

        <div className="bg-white rounded-md shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Hospital CMS</h2>
              {canCreateHospital && (
                <div className="flex items-center gap-2">
                  {canBulkUploadHospitals && (
                    <>
                      <button
                        onClick={handleDownloadTemplate}
                        disabled={templateLoading}
                        className="px-3 py-2 border border-green-600 text-green-700 rounded-md text-sm hover:bg-green-50 flex items-center gap-2 transition-colors disabled:opacity-50"
                        title="Download Excel template for bulk hospital onboarding"
                      >
                        <FaFileExcel className="w-3 h-3" />
                        {templateLoading ? "Downloading..." : "Excel Format"}
                      </button>

                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadLoading}
                        className="px-3 py-2 border border-orange-500 text-orange-600 rounded-md text-sm hover:bg-orange-50 flex items-center gap-2 transition-colors disabled:opacity-50"
                        title="Upload filled Excel to bulk-add hospitals"
                      >
                        <FaUpload className="w-3 h-3" />
                        {uploadLoading
                          ? (uploadJob ? `Processing ${uploadJob.percent || 0}%` : "Uploading...")
                          : "Upload Excel"}
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        className="hidden"
                        onChange={handleFileSelected}
                      />
                    </>
                  )}

                  <button
                    onClick={() => navigate("/subscriber-wallet/hospitals/add")}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 flex items-center gap-2 transition-colors"
                  >
                    <FaPlus className="w-3 h-3" />
                    Add Hospital
                  </button>
                </div>
              )}
            </div>

            {uploadJob && !uploadResult && (
              <div className="mb-4 p-4 rounded-md border bg-blue-50 border-blue-200 text-sm">
                <p className="font-semibold text-blue-900 mb-2">
                  Bulk upload in progress — {uploadJob.rows_processed || 0} / {uploadJob.total_rows || 0} rows ({uploadJob.percent || 0}%)
                </p>
                <div className="w-full bg-blue-100 rounded h-2 overflow-hidden">
                  <div
                    className="bg-blue-600 h-2 transition-all duration-500"
                    style={{ width: `${uploadJob.percent || 0}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-blue-800">
                  You can leave this page — the job continues running on the server. Reopen and we'll pick up where it left off.
                </p>
              </div>
            )}

            {uploadResult && (
              <div className={`mb-4 p-4 rounded-md border text-sm ${uploadResult.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {uploadResult.success ? (
                      <>
                        <p className="font-semibold text-green-800 mb-1">
                          Upload complete — {uploadResult.data?.rows_created ?? 0} hospital{(uploadResult.data?.rows_created ?? 0) !== 1 ? "s" : ""} added.
                          {(uploadResult.data?.rows_failed ?? 0) > 0 && (
                            <span className="ml-2 text-orange-700">{uploadResult.data.rows_failed} failed.</span>
                          )}
                          {(uploadResult.data?.rows_skipped_duplicates ?? 0) > 0 && (
                            <span className="ml-2 text-gray-600">{uploadResult.data.rows_skipped_duplicates} duplicate{uploadResult.data.rows_skipped_duplicates !== 1 ? "s" : ""} skipped.</span>
                          )}
                        </p>
                        {uploadResult.data?.error_file_url && (
                          <a
                            href={uploadResult.data.error_file_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 mt-2 text-orange-700 hover:text-orange-900 underline text-sm"
                          >
                            <FaFileExcel className="w-3 h-3" /> Download error report
                          </a>
                        )}
                      </>
                    ) : (
                      <p className="text-red-700 font-medium">
                        {uploadResult.error || uploadResult.data?.error_message || "Bulk upload failed."}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => { setUploadResult(null); setUploadJob(null); }}
                    className="ml-4 text-gray-400 hover:text-gray-600 text-lg leading-none"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter by hospital name"
                  className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                />
              </div>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter by city"
                  className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  value={cityInput}
                  onChange={(e) => setCityInput(e.target.value)}
                />
              </div>
            </div>
          </div>

          {error && <div className="px-6 py-3 text-sm text-red-700 bg-red-50 border-b border-red-100">{error}</div>}

          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-[25%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="w-[13%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                  <th className="w-[12%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State</th>
                  <th className="w-[13%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="w-[7%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beds</th>
                  <th className="w-[13%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated By</th>
                  <th className="w-[13%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  <th className="w-[4%] px-6 py-3" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {hospitals.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                      {loading ? "Loading..." : "No hospitals found"}
                    </td>
                  </tr>
                ) : (
                  hospitals.map((hospital) => {
                    const hospitalId = hospital.id || hospital.hospital_id;
                    const active = isHospitalActive(hospital);
                    return (
                      <tr
                        key={hospitalId}
                        className={`hover:bg-gray-50 cursor-pointer ${active ? "" : "bg-gray-50/60"}`}
                        onClick={() =>
                          openHospital(`/subscriber-wallet/hospitals/${encodeURIComponent(hospitalId)}`, {
                            state: { hospital },
                          })
                        }
                      >
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 truncate">
                          <span className={active ? "" : "text-gray-400"}>{hospital.name || "N/A"}</span>
                        </td>

                        <td className="px-6 py-4 text-sm text-gray-500 truncate">
                          {hospital?.address_abstract?.[0]?.city || "N/A"}
                        </td>

                        <td className="px-6 py-4 text-sm text-gray-500 truncate">
                          {hospital?.address_abstract?.[0]?.state || "N/A"}
                        </td>

                        <td className="px-6 py-4 text-sm text-gray-500 truncate">
                          {hospital.hospital_type || "N/A"}
                        </td>

                        <td className="px-6 py-4 text-sm text-gray-500">
                          {hospital.no_of_bed ?? "N/A"}
                        </td>

                        <td className="px-6 py-4 text-sm text-gray-500 truncate">
                          {hospital.user?.first_name || "N/A"}
                        </td>

                        <td className="px-6 py-4 text-sm">
                          {canCreateHospital && (
                            <div
                              className="flex items-center gap-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                role="switch"
                                aria-checked={active}
                                aria-label={active ? "Disable hospital" : "Enable hospital"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setStatusError("");
                                  setStatusTarget(hospital);
                                }}
                                className="relative inline-flex flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-blue-500 rounded-full"
                              >
                                <span
                                  className={`block w-10 h-5 rounded-full transition-colors ${
                                    active ? "bg-green-500" : "bg-gray-300"
                                  }`}
                                />
                                <span
                                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                    active ? "translate-x-5" : "translate-x-0"
                                  }`}
                                />
                              </button>
                              <span className={`text-xs font-medium ${active ? "text-green-600" : "text-gray-400"}`}>
                                {active ? "Active" : "Disabled"}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-gray-400">
                          <FaChevronRight />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {pagination.total > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.items + 1}-{Math.min(pagination.page * pagination.items, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  disabled={pagination.page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page === 1 ? "text-gray-400 border-gray-200 cursor-not-allowed" : "text-gray-700 hover:bg-gray-50 border-gray-300"}`}
                >
                  Prev
                </button>
                <button
                  disabled={pagination.page >= (pagination.total_pages || 1)}
                  onClick={() => setPage((p) => Math.min(pagination.total_pages || 1, p + 1))}
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page >= (pagination.total_pages || 1) ? "text-gray-400 border-gray-200 cursor-not-allowed" : "text-gray-700 hover:bg-gray-50 border-gray-300"}`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmStatusModal
        hospital={statusTarget}
        disable={statusTarget ? isHospitalActive(statusTarget) : false}
        submitting={statusSubmitting}
        error={statusError}
        onCancel={() => {
          if (!statusSubmitting) {
            setStatusTarget(null);
            setStatusError("");
          }
        }}
        onConfirm={handleConfirmStatusToggle}
      />
    </div>
  );
};

export default HospitalList;
